import { Heart, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SearchResult } from '@/lib/osint-api';

interface ResultCardProps {
  result: SearchResult;
  isFavorited?: boolean;
  onFavorite?: (resultId: string) => void;
}

export default function ResultCard({
  result,
  isFavorited = false,
  onFavorite,
}: ResultCardProps) {
  const getConfidenceBadgeColor = (score: number) => {
    if (score >= 0.9) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
    if (score >= 0.75) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow">
      <div className="p-4">
        <div className="flex gap-4 mb-4">
          <img
            src={result.profileImageUrl}
            alt={result.profileName}
            className="w-12 h-12 rounded-full"
          />
          <div className="flex-1">
            <h3 className="font-semibold text-sm">{result.profileName}</h3>
            <p className="text-xs text-muted-foreground">{result.platform}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onFavorite?.(result.id)}
            className="h-8 w-8 p-0"
          >
            <Heart
              className="w-4 h-4"
              fill={isFavorited ? 'currentColor' : 'none'}
              color={isFavorited ? 'red' : 'currentColor'}
            />
          </Button>
        </div>

        <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
          {result.profileBio}
        </p>

        <div className="flex items-center justify-between">
          <Badge className={getConfidenceBadgeColor(result.confidenceScore)}>
            {(result.confidenceScore * 100).toFixed(0)}% match
          </Badge>
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="h-8 px-2 gap-1"
          >
            <a href={result.profileUrl} target="_blank" rel="noopener noreferrer">
              View
              <ExternalLink className="w-3 h-3" />
            </a>
          </Button>
        </div>
      </div>
    </Card>
  );
}
